/*eslint complexity: ["error", 52]*/
/*eslint max-lines-per-function: ["error", 230]*/
import React from 'react';
import { Trash } from 'react-bootstrap-icons';
import * as Icon from 'react-bootstrap-icons';
import moment from 'moment/moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { GetAttachmentDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import momencik from '../../../common/momencik';
import {
  GridColumn,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import { Grid } from '../../../common/components/Grid/Grid';
import {
  AttachmentAutomaticDescriptionObj,
  AttachmentAutomaticNoteObj,
  AttachmentObjectType,
  AttachmentObjectTypeObj,
} from '../../../common/pokCore/validation/enums';
import SelectOptionInfo from '../Selects/SelectOptionInfo';
import oneliner from '../../../common/oneliner';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { NewWindowButton } from '../Contracts/NewWindowButton';

import { AttachmentViewTypes } from './AttachmentViewTypes';
import useIsEditAttachmentAllowedProjects from './useIsEditAttachmentAllowedProjects';

export const AttachmentsGrid = (props: {
  documents: GetAttachmentDto[];
  onClick: (doc: GetAttachmentDto) => void;
  onDelete: (id: string) => Promise<void>;
  readOnly?: boolean;
  type?: AttachmentViewTypes;
  withContract?: boolean;
}) => {
  const { t, tk } = useTranslation('commonLng');
  const isDeleteAllow = useIsEditAttachmentAllowedProjects(props.type);
  const isNote = props.type === 'notes';
  const pok = usePokCore();
  const lang = pok.getLanguage();

  const COLUMNS: GridColumn[] = [
    ...(isNote ? [] : [{ header: t(tk.attachment.type), property: 'type' }]),
    { header: t(tk.common.title), property: 'description' },
    { header: t(tk.common.name), property: 'name' },
    ...(isNote
      ? []
      : [{ header: t(tk.attachment.invoicing), property: 'client.name' }]),
    ...(isNote
      ? []
      : [
          {
            header: t(tk.attachment.positionsSum),
            property: 'positionsSum',
            noSorting: true,
          },
        ]),
    {
      header: t(tk.prLang.relatedEstimatesByMonth),
      property: 'estimateByMonths',
    },
    ...(props.withContract
      ? [
          {
            header: t(tk.common.contract),
            property: 'contract',
            noSorting: true,
          },
        ]
      : []),
    { header: t(tk.common.addDate), property: 'date' },
    ...(isNote
      ? []
      : [{ header: t(tk.attachment.relatedTo), property: 'relationType' }]),
    { header: t(tk.attachment.note), property: 'note', noSorting: true },
    { header: t(tk.common.creator), property: 'creator', noSorting: true },
    ...(props.readOnly
      ? []
      : [{ header: '', property: 'deleteLink', noSorting: true }]),
  ];
  const NOTE_MAX_LENGTH = 150;
  const data = (props.documents || []).map(document => {
    const didCreatedLessThanHourAgo = !moment(document.creationTs).isBefore(
      moment().subtract(1, 'hours'),
    );

    const positionsSum = document?.estimateItems?.length
      ? numberFormatter(
          document?.estimateItems?.reduce(
            (acc, { estimateItem }) =>
              mathUtils.add(acc, estimateItem?.purchaseNetTotal || 0),
            0,
          ),
          true,
        )
      : '';
    const isContract =
      document.enObjectType === AttachmentObjectType.SignedContract;

    return {
      key: document.id,
      values: {
        automatic: document.automatic ? true : false,
        name: (
          <>
            {document.name === 'Notatka'
              ? t(tk.attachment.note)
              : document.name}{' '}
            {document.automatic && (
              <OverlayTrigger
                overlay={<Tooltip>{t(tk.attachment.automatic)}</Tooltip>}
              >
                <Icon.Magic size={20} className="ms-2 icon-blue" />
              </OverlayTrigger>
            )}
          </>
        ),
        description: document.automatic
          ? t(
              AttachmentAutomaticDescriptionObj[
                document.description as keyof typeof AttachmentAutomaticDescriptionObj
              ],
            )
          : document.description,
        note: document.note && (
          <>
            {oneliner(
              document.automatic
                ? t(
                    AttachmentAutomaticNoteObj[
                      document.note as keyof typeof AttachmentAutomaticNoteObj
                    ],
                  )
                : document.note,
              NOTE_MAX_LENGTH,
            )}
            <SelectOptionInfo
              label={
                document.automatic
                  ? t(
                      AttachmentAutomaticNoteObj[
                        document.note as keyof typeof AttachmentAutomaticNoteObj
                      ],
                    )
                  : document.note
              }
              title={t(tk.attachment.note)}
              placement="auto"
            />
          </>
        ),
        sortDate: new Date(document.creationTs || new Date()).getTime(),
        date: momencik(document.creationTs),
        type:
          document.enObjectType === AttachmentObjectType.SignedContract
            ? t(tk.common.contract)
            : lang === 'PL'
              ? document.dictionaryType?.value
              : document.dictionaryType?.valueEn,
        relationType: t(
          AttachmentObjectTypeObj[
            document.enObjectType as unknown as AttachmentObjectType
          ],
        ),
        creator: document.creator?.name,
        'client.name': isContract
          ? document?.contract?.client?.name
          : document?.client?.name,
        positionsSum:
          isContract && document?.contract?.amount
            ? numberFormatter(document?.contract?.amount, true)
            : positionsSum,
        estimateByMonths: document?.estimateByMonths
          ?.map(
            ebm =>
              ebm?.estimateByMonth?.profile?.name +
              ' ' +
              momencik(ebm?.estimateByMonth?.date, 'YYYY-MM'),
          )
          .join(', '),
        contract: isContract && document?.contract?.id && (
          <>
            <NewWindowButton contractId={document?.contract.id} />
            {document?.contract?.number || ''}
            {document?.contract?.tv && (
              <OverlayTrigger
                overlay={<Tooltip>{t(tk.common.contractTv)}</Tooltip>}
              >
                <Icon.DisplayFill size={20} className="ms-2 icon-blue" />
              </OverlayTrigger>
            )}
          </>
        ),

        deleteLink: (isDeleteAllow || didCreatedLessThanHourAgo) &&
          !document.automatic &&
          document.enObjectType !== AttachmentObjectType.SignedContract && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              className="d-flex justify-content-center"
              onClick={e => e.stopPropagation()}
            >
              <ConfirmationButton
                variant="outline-danger"
                confirmation={
                  isNote
                    ? t(tk.attachment.askDeleteNote)
                    : t(tk.attachment.askDeleteAttachment)
                }
                tooltip={
                  isNote
                    ? t(tk.attachment.deleteNote)
                    : t(tk.attachment.deleteAttachment)
                }
                onOK={() => props.onDelete(document.id)}
              >
                <Trash size={16} />
              </ConfirmationButton>
            </div>
          ),
      },
      tdCssClass: {
        positionsSum: 'amounts-align-right',
      },
      orderByNumberValues: {
        date: new Date(document.creationTs || new Date()).getTime(),
      },
    };
  }) as unknown as GridRecord[];

  return (
    <Grid
      data={data}
      columns={COLUMNS}
      showFilter={true}
      onRowClick={
        props.readOnly
          ? undefined
          : (id: string) => {
              const foundDocument = props.documents.find(doc => doc.id === id);
              if (
                foundDocument &&
                foundDocument.enObjectType !==
                  AttachmentObjectType.SignedContract
              ) {
                props.onClick(foundDocument);
              }
            }
      }
      emptyGridMessage={
        isNote ? t(tk.attachment.noNotes) : t(tk.attachment.noAttachments)
      }
      initialOrderColumn="sortDate"
      initialOrderDirection="DESC"
    />
  );
};
